import {useMantineTheme} from '@mantine/core';
import fileDownload from 'js-file-download';
import {useCallback} from 'react';
import type {MessageResponse} from 'src/api/ProactApiClient';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {printTranscript} from './printTranscript';
import {resolvePrintChartColors} from './printChartPlan';
import type {ChatTranscript} from './transcript';
import {
  UNTITLED_TRANSCRIPT_TITLE,
  buildTranscriptEntries,
  buildTranscriptMarkdown,
  transcriptFileStem,
} from './transcript';

export interface UseChatTranscriptExportArgs {
  title: string | undefined;
  messages: MessageResponse[] | undefined;
  isStreaming: boolean;
}

export interface UseChatTranscriptExportResult {
  canExport: boolean;
  downloadMarkdown: () => void;
  print: () => void;
}

export const useChatTranscriptExport = ({
  title,
  messages,
  isStreaming,
}: UseChatTranscriptExportArgs): UseChatTranscriptExportResult => {
  const theme = useMantineTheme();
  const {setAlert} = useAlert();

  const canExport = !isStreaming && (messages?.length ?? 0) > 0;

  const buildTranscript = useCallback(
    (): ChatTranscript => ({
      title: title?.trim() || UNTITLED_TRANSCRIPT_TITLE,
      exportedAt: new Date(),
      entries: buildTranscriptEntries(messages ?? []),
    }),
    [messages, title]
  );

  const downloadMarkdown = useCallback(() => {
    try {
      const transcript = buildTranscript();
      fileDownload(
        buildTranscriptMarkdown(transcript),
        `${transcriptFileStem(transcript.title, transcript.exportedAt)}.md`,
        'text/markdown;charset=utf-8'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [buildTranscript, setAlert]);

  const print = useCallback(() => {
    try {
      const transcript = buildTranscript();
      printTranscript(transcript, {
        documentTitle: transcript.title,
        palette: {
          text: theme.black,
          dimmed: theme.colors.gray[7],
          border: theme.colors.gray[4],
          surface: theme.colors.gray[1],
          paper: theme.white,
          chartColors: resolvePrintChartColors(theme),
          fontFamily: theme.fontFamily ?? 'sans-serif',
          monospaceFontFamily: theme.fontFamilyMonospace ?? 'monospace',
        },
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [buildTranscript, setAlert, theme]);

  return {canExport, downloadMarkdown, print};
};
